import {
  createReducer,
  on,
  Action,
  ActionReducerMap,
  combineReducers,
} from "@ngrx/store";
import { EntityState, EntityAdapter, createEntityAdapter } from "@ngrx/entity";

import * as GenericActions from "./pipeline-generic.actions";

export const PIPELINE_GENERIC_FEATURE_KEY = "pipelineGeneric";
export const PIPELINE_GENERIC_PIPELINE_SECTION_FEATURE_KEY = "pipelineSection";
export const PIPELINE_GENERIC_ACCOUNTABLE_FUNCTION_LINK_FEATURE_KEY = "accountableFunction";
export const PIPELINE_GENERIC_PIPELINE_ASSET_LINK_FEATURE_KEY =
  "pipelineAsset";
export const PIPELINE_GENERIC_LAND_TITLE_FEATURE_KEY = "landTitle";
export const PIPELINE_GENERIC_OPLLPTS_HISTORY_FEATURE_KEY = "opllPtsHistory";

export function Generic_FeatureKey(key: string) {
  return key;
}

export enum UploadStatus {
  Ready = "Ready",
  Requested = "Requested",
  Started = "Started",
  Failed = "Failed",
  Completed = "Completed",
}

export interface GenericState extends EntityState<any> {
  selectedId?: string | number; // which Model Type record has been selected
  loaded: boolean; // has the Model list been loaded
  error?: string | null; // last none error (if any)
  status: UploadStatus;
  progress: number | null;
}

export interface GenericPartialState {
  readonly [PIPELINE_GENERIC_FEATURE_KEY]: GenericState;
  readonly [PIPELINE_GENERIC_PIPELINE_SECTION_FEATURE_KEY]: GenericState;
  readonly [PIPELINE_GENERIC_ACCOUNTABLE_FUNCTION_LINK_FEATURE_KEY]: GenericState;
  readonly [PIPELINE_GENERIC_PIPELINE_ASSET_LINK_FEATURE_KEY]: GenericState;
  readonly [PIPELINE_GENERIC_LAND_TITLE_FEATURE_KEY]: GenericState;
  readonly [PIPELINE_GENERIC_OPLLPTS_HISTORY_FEATURE_KEY]: GenericState;
}

export const GenericAdapter: EntityAdapter<any> = createEntityAdapter<
  any
>({
  selectId: (generic) => generic.id,
  sortComparer: false,
});

export const initialState: GenericState = GenericAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  error: null,
  selectedId: null,
  status: UploadStatus.Ready,
  progress: null,
});

export const GenericReducer = (type: string) =>
  createReducer(
    initialState,
    on(GenericActions.loadGeneric(type), (state) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.loadGenericSuccess(type), (state, { returnedData }) =>
      GenericAdapter.setAll(returnedData, { ...state, loaded: false })),
    on(GenericActions.loadGenericFailure(type), (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })),
    on(GenericActions.loadGenericWithoutId(type), (state) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.loadGenericWithoutIdSuccess(type), (state, { returnedData }) =>
      GenericAdapter.setAll(returnedData, { ...state, loaded: false })),
    on(GenericActions.loadGenericWithoutIdFailure(type), (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })),
    on(GenericActions.loadByIdGeneric(type), (state) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.loadByIdGenericSuccess(type), (state, { returnedData }) =>
      GenericAdapter.setAll(returnedData, { ...state, loaded: false })),
    on(GenericActions.loadByIdGenericFailure(type), (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })),
    on(GenericActions.addGeneric(type), (state) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.addGenericSuccess(type), (state, { returnedData }) =>
      GenericAdapter.upsertMany(returnedData, { ...state, loaded: false })),
    on(GenericActions.addGenericFailure(type), (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })),
    on(GenericActions.updateGeneric(type), (state) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.updateGenericSuccess(type), (state, { returnedData }) =>
      GenericAdapter.upsertMany(returnedData, { ...state, loaded: false })),
    on(GenericActions.updateGenericFailure(type), (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })),
    on(GenericActions.removeGeneric(type), (state, inputtype) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.removeGenericSuccess(type), (state, { ids }) =>
      GenericAdapter.removeMany(ids, { ...state, loaded: false })),
    on(GenericActions.removeGenericFailure(type), (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })),
    on(GenericActions.loadGenericClearState(type), (state) => ({
      ...state,
      loaded: true,
      error: null,
    })),
    on(GenericActions.loadGenericClearStateSuccess(type), (state, { returnedData }) =>
      GenericAdapter.setAll(returnedData, { ...state, loaded: false })),

  );

export const reducer = (state: GenericState | undefined, action: Action) =>
  GenericReducer("Country")(state, action);

export const pipelineSectionReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("PipelineSection")(state, action);

export const accountableFunctionReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("AccountableFunctionLink2")(state, action);

export const pipelineAssetReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("PipelineAssetLink2")(state, action);

export const landTitleReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("LandTitle")(state, action);

export const opllPtsHistoryReducer = (
  state: GenericState | undefined,
  action: Action,
) => GenericReducer("OpllPtsHistory")(state, action);
